import * as React from "react";
import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { ChevronDown } from "lucide-react";

import { cn } from "@dashai/lib/utils";

// Site navigation in the disclosure pattern: each trigger discloses a panel of
// links. No viewport is rendered, so every panel opens under its own trigger.
//
// Panels open and close on click and keyboard only. Radix also opens a panel
// when the pointer rests on its trigger, and instantly while another panel is
// open, so the click that follows would toggle it shut again. Cancelling the
// pointer-move and pointer-leave events skips Radix's hover handlers. A
// caller's own handlers still run first.
type PointerHandlers = {
  onPointerMove?: React.PointerEventHandler;
  onPointerLeave?: React.PointerEventHandler;
};
const cancelHover = ({ onPointerMove, onPointerLeave }: PointerHandlers): Required<PointerHandlers> => ({
  onPointerMove: (event) => {
    onPointerMove?.(event);
    event.preventDefault();
  },
  onPointerLeave: (event) => {
    onPointerLeave?.(event);
    event.preventDefault();
  },
});

const NavigationMenu = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Root ref={ref} className={cn("relative flex items-center", className)} {...props} />
));
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

const NavigationMenuList = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.List ref={ref} className={cn("flex list-none items-center", className)} {...props} />
));
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuItem = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Item ref={ref} className={cn("relative", className)} {...props} />
));
NavigationMenuItem.displayName = NavigationMenuPrimitive.Item.displayName;

const NavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Trigger
    ref={ref}
    className={cn("group inline-flex items-center justify-center outline-none", className)}
    {...props}
    {...cancelHover(props)}
  >
    {children}
    <ChevronDown
      className="h-3 w-3 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180"
      aria-hidden="true"
    />
  </NavigationMenuPrimitive.Trigger>
));
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

const NavigationMenuContent = React.forwardRef<
  React.ElementRef<typeof NavigationMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content>
>(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Content
    ref={ref}
    className={cn(
      "absolute left-0 top-full z-50 mt-2.5 rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-lg",
      className,
    )}
    {...props}
    {...cancelHover(props)}
  />
));
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

const NavigationMenuLink = NavigationMenuPrimitive.Link;

export {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
};
